import React, { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import { Form, Input, Select } from "formik-antd";
import i18n from "i18n";
import { CloseOutlined } from "@ant-design/icons";
import { Spin, Row, Col, Button, Tabs, Checkbox, Tooltip, Switch } from "antd";
import SaveButton from "components/SaveButton";
import { connect } from "react-redux";
import { RootState } from "store/store";
import {
  Surveys,
  SurveysEditForm,
  LogoUploadResult,
  LanguageModel,
  LanguageSettings,
  SurveyImpressionModel,
  SelectableImpressionModel,
  FortuneSettingsType,
  SurveyBgColorHexTemplateForm,
  SSOClient,
  IndustryCategory,
} from "api/sms-api";
import {
  addSurveys,
  editSurveySettings,
  getSurvey,
  uploadLogo,
  getLanguageModelListAll,
  getLanguageSettingsListAll,
  getImpressionModels,
  getSelectableImpressionModels,
  getFortuneSettingsTypeListAll,
  setSurveyBgColorTemplates,
  getSurveyBgColorTemplates,
  getIndustryCategories,
} from "./SurveyActions";
import { useHistory } from "react-router-dom";
import { pushTo } from "utils/navigation";
import qs from "qs";
import RandomDisplaySetting, {
  randomDisplayOptions,
} from "./RandomDisplaySettingModal";
import { Page, setPage } from "features/appstate/AppStateSlice";
import {
  MODE_SURVEY,
  surveyTypeStore,
  proofReadingModel,
  ImpressionPromptMode,
  UI_VERSION_OPTIONS,
  UI_V2_ANSWER_TIME_OPTIONS,
} from "../../constants";
import RichEditor from "components/RichEditor";
import SelectGptModelModal from "./SelectGptModelModal";
import SelectImpressionPromptModal from "./SelectImpressionPromptModal";
import SelectLanguageModal from "./SelectLanguageModal";
import { surveySettingsSchema } from "validation";
import { clearImpressionModels } from "./SurveySlice";
import EmailOptionModal from "./EmailOptionModal";
import { SketchPicker } from "react-color";
import { FolderOpenOutlined } from "@ant-design/icons";

const DEFAULT_RANDOM_COUNT = 2;
const DEFAULT_LANGUAGE_MODEL = "4";
const DEFAULT_LANGUAGE_SETTING = "en";

interface SurveySettingsProps {
  setPage: (page: Page) => void;

  survey: Surveys;
  getLoading: boolean;
  getSurvey: (surveyId: string) => void;

  impressionModels: Array<SurveyImpressionModel>;
  impressionModelsLoading: boolean;
  getImpressionModels: (surveyId: string) => void;
  clearImpressionModels: () => void;

  selectableImpressionModels: Array<SelectableImpressionModel>;
  selectableImpressionModelsLoading: boolean;
  getSelectableImpressionModels: () => void;

  addSurveys: (params: SurveysEditForm) => Promise<void>;
  editSurveySettings: (
    surveyId: string,
    params: SurveysEditForm
  ) => Promise<void>;

  uploadLogo: (file: File) => Promise<LogoUploadResult | undefined>;

  languageModel: Array<LanguageModel>;
  getLanguageModelListAll: () => void;
  getLanguageModelLoading: boolean;

  languageSettings: Array<LanguageSettings>;
  getLanguageSettingsListAll: () => void;
  getLanguageSettingsListLoading: boolean;
  getFortuneSettingsTypeListAll: () => void;
  getFortuneSettingsTypeListLoading: boolean;
  fortuneSettingsTypeList: Array<FortuneSettingsType>;

  setSurveyBgColorTemplates: (params: SurveyBgColorHexTemplateForm) => void;
  getSurveyBgColorTemplates: (clientToken: string) => void;
  colorTemplates: string | undefined;
  ssoClient?: SSOClient;

  industryCategories: IndustryCategory[];
  getIndustryCategories: () => void;
}
interface ColorPickerPopupProps {
  visible: boolean;
  onClose: () => void;
  color: string;
  onChangeComplete: (color: { hex: string }) => void;
  top?: number;
  left?: number;
}
// テンプレート型定義
type ColorTemplate = {
  surveyBgColor: string;
  completeBgColor: string;
};

const SurveySettings: React.FC<SurveySettingsProps> = (props) => {
  const history = useHistory();
  const [isRandomDisplayModalOpen, setIsRandomDisplayModalOpen] = useState(
    false
  );
  const [isLanguageModel, setIsLanguageModel] = useState(false);
  const [isImpressionPrompt, setIsImpressionPrompt] = useState(false);
  const [isLanguageSettings, setIsLanguageSettings] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [isEmailOptionsModalOpen, setIsEmailOptionsModalOpen] = useState(false);

  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const surveyId = parsed.id ? parsed.id.toString() : "";
  const type = parsed.type ? parsed.type.toString() : "";
  const surveyType = parsed.surveyType
    ? (parsed.surveyType as string)
    : surveyTypeStore;

  const [surveyBgColorPicker, setSurveyBgColorPicker] = useState(false);
  const [
    surveyCompleteBgColorPicker,
    setSurveyCompleteBgColorPicker,
  ] = useState(false);
  const [editScreenCardColorPicker, setEditScreenCardColorPicker] = useState(
    false
  );
  const [localColorTemplates, setLocalColorTemplates] = useState<
    ColorTemplate[]
  >([]);
  const ColorPickerPopup: React.FC<ColorPickerPopupProps> = ({
    visible,
    onClose,
    color,
    onChangeComplete,
    top = 40,
    left = 0,
  }) => {
    if (!visible) return null;
    return (
      <>
        {/* オーバーレイ（クリックで閉じる） */}
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 999,
          }}
          onClick={onClose}
        />
        {/* ピッカー本体 */}
        <div
          style={{
            position: "absolute",
            top: top,
            left: left,
            zIndex: 1000,
            width: "300px",
          }}
        >
          <SketchPicker color={color} onChangeComplete={onChangeComplete} />
        </div>
      </>
    );
  };

  useEffect(() => {
    props.setPage(Page.surveySettings);
  }, [props.setPage]);

  useEffect(() => {
    props.getIndustryCategories();
  }, [props.getIndustryCategories]);

  useEffect(() => {
    if (surveyId) {
      props.getSurvey(surveyId);
    }
  }, [props.getSurvey]);

  useEffect(() => {
    if (surveyId) {
      props.getImpressionModels(surveyId);
    }
    return () => {
      props.clearImpressionModels();
    };
  }, [props.getImpressionModels]);

  useEffect(() => {
    props.getSelectableImpressionModels();
  }, [props.getSelectableImpressionModels]);

  useEffect(() => {
    if (props.survey && props.survey.logoUrl) {
      setLogoUrl(props.survey.logoUrl);
    }
  }, [props.survey]);

  useEffect(() => {
    props.getLanguageModelListAll();
  }, [props.getLanguageModelListAll]);

  useEffect(() => {
    props.getLanguageSettingsListAll();
  }, [props.getLanguageSettingsListAll]);

  useEffect(() => {
    props.getFortuneSettingsTypeListAll();
  }, [props.getFortuneSettingsTypeListAll]);

  useEffect(() => {
    props.getSurveyBgColorTemplates(
      props.ssoClient ? props.ssoClient.clientToken : ""
    );
  }, [props.getSurveyBgColorTemplates, props.ssoClient?.clientToken]);

  useEffect(() => {
    if (props.colorTemplates && typeof props.colorTemplates === "string") {
      try {
        const parsed = JSON.parse(props.colorTemplates);
        if (Array.isArray(parsed)) {
          setLocalColorTemplates(parsed);
        }
      } catch (e) {
        setLocalColorTemplates([]);
      }
    }
  }, [props.colorTemplates]);

  if (
    props.getLoading ||
    props.impressionModelsLoading ||
    props.selectableImpressionModelsLoading ||
    props.getLanguageModelLoading ||
    props.getLanguageSettingsListLoading ||
    props.getFortuneSettingsTypeListLoading
  ) {
    return <Spin />;
  }

  const handleSubmit = async (form: SurveysEditForm) => {
    form.logoUrl = logoUrl;
    let targetRoute = "/survey";
    if (type === "new" || type === "template") {
      if (props.survey && props.survey.id) {
        await props.editSurveySettings(props.survey.id, form);
      } else {
        await props.addSurveys(form);
      }
      if (type === "new") {
        targetRoute = `/${Page.survey}/edit`;
      } else if (type === "template") {
        targetRoute = `/${Page.survey}/template`;
      }
      pushTo(history, targetRoute, {
        mode: MODE_SURVEY,
        surveyType: parsed.surveyType || surveyTypeStore,
        type: type,
      });
    } else {
      await props.editSurveySettings(surveyId, form);
      pushTo(history, targetRoute, {
        mode: MODE_SURVEY,
        surveyType: parsed.surveyType || surveyTypeStore,
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const result = await props.uploadLogo(e.target.files[0]);

    if (result) {
      setLogoUrl(result.url);
    }
  };

  const handleFileRemove = async () => {
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    setLogoUrl("");
  };

  const initialValues = {
    uiVersion: props.survey?.uiVersion || "v2",
    title: (props.survey && props.survey.title) || "",
    topPageTitle: (props.survey && props.survey.topPageTitle) || "",
    topPageNote:
      (props.survey && props.survey.topPageNote) ||
      i18n.t("survey.defaultTopPageNote"),
    logoUrl: logoUrl || "",
    publish: (props.survey && props.survey.publish) || false,
    randomDisplay: props.survey?.randomDisplay || false,
    randomDisplayCount:
      props.survey?.randomDisplayCount || DEFAULT_RANDOM_COUNT,
    customPrompt: props.survey?.customPrompt || false,
    customPromptText: props.survey?.customPromptText || "",
    randomQuestionsOrder: props.survey?.randomQuestionsOrder ?? false,
    isLanguageModelSelectable: props.survey?.isLanguageModelSelectable || false,
    languageModelId:
      (props.survey?.languageModelId ||
        props.languageModel.find((item) => item.code === DEFAULT_LANGUAGE_MODEL)
          ?.id) ??
      null,
    surveyType,
    isProofreading: props.survey?.isProofreading || false,
    isLanguageSettingsSelectable:
      props.survey?.settingOptions.isLanguageSettingsSelectable || false,
    languageSettingsId:
      (props.survey?.settingOptions.languageSettingsId ||
        props.languageSettings.find(
          (item) => item.code === DEFAULT_LANGUAGE_SETTING
        )?.id) ??
      null,
    impressionPromptOptionsWithModels: {
      isImpressionPrompt:
        props.survey?.settingOptions.impressionPromptOptions
          .isImpressionPrompt || false,
      impressionPromptMode:
        props.survey?.settingOptions.impressionPromptOptions
          .impressionPromptMode || ImpressionPromptMode.Random,
      impressionPromptModels:
        props.impressionModels.length > 0
          ? props.impressionModels.map(
              (model) => model.selectableImpressionModelsId
            )
          : props.selectableImpressionModels.map(
              (model) => model.selectableImpressionModelId
            ),
    },
    isRandomGenerateModel: props.survey?.isRandomGenerateModel || false,
    isEmailOption: props.survey?.isEmailOption || false,
    emailDelayMinutes: props.survey?.emailDelayMinutes || 0,
    isVoc: props.survey?.isVoc || false,
    fortuneSettingsTypeId:
      props.survey?.fortuneSettingsTypeId ||
      props.fortuneSettingsTypeList[0]?.id,
    bgColorHex: props.survey?.bgColorHex || null,
    completeBgColorHex: props.survey?.completeBgColorHex || null,
    isViewProgressBar:
      typeof props.survey?.isViewProgressBar === "boolean"
        ? props.survey.isViewProgressBar
        : true,
    voiceInputIconSize: props.survey?.voiceInputIconSize || "normal",
    industryCategoryId: props.survey?.industryCategoryId ?? "",
    industrySubcategory: props.survey?.industrySubcategory || "",
    isViewLogoAllPages: props.survey?.isViewLogoAllPages || false,
    generateTemperature: props.survey?.generateTemperature || 0.5,
    vocType: props.survey?.vocType || 1,
    vocTopPageNote:
      (props.survey && props.survey.vocTopPageNote) ||
      i18n.t("survey.defaultVocTopPageNote"),
    vocCompletedNote:
      (props.survey && props.survey.vocCompletedNote) ||
      i18n.t("survey.defaultVocCompletedNote"),
    isViewAnswerTimeUiV2: props.survey?.isViewAnswerTimeUiV2 ?? true,
    answerTimeMinutesUiV2: props.survey?.answerTimeMinutesUiV2 || 1,
    topPageNoteUiV2:
      (props.survey && props.survey.topPageNoteUiV2) ||
      i18n.t("survey.defaultTopPageNoteUiV2"),
    completionTitleUiV2:
      (props.survey && props.survey.completionTitleUiV2) ||
      i18n.t("survey.defaultCompletionTitleUiV2"),
    completionTextUiV2:
      (props.survey && props.survey.completionTextUiV2) ||
      i18n.t("survey.defaultCompletionTextUiV2"),
    isEditScreenCardUiV2: props.survey?.isEditScreenCardUiV2 ?? true,
    editScreenCardBgColorHexUiV2:
      props.survey?.editScreenCardBgColorHexUiV2 || "#fffbe6",
    editScreenCardTitleUiV2:
      (props.survey && props.survey.editScreenCardTitleUiV2) ||
      i18n.t("survey.defaultEditScreenCardTitleUiV2"),
    editScreenCardTextUiV2:
      (props.survey && props.survey.editScreenCardTextUiV2) ||
      i18n.t("survey.defaultEditScreenCardTextUiV2"),
  };

  const getImpressionModeText = (
    isImpressionPrompt: boolean,
    impressionPromptMode: ImpressionPromptMode
  ) => {
    if (!isImpressionPrompt) return null;

    return impressionPromptMode === ImpressionPromptMode.Random
      ? i18n.t("survey.impressionPromptMode.random")
      : i18n.t("survey.impressionPromptMode.always");
  };

  const handleSaveTemplate = (bgColor: string, completeColor: string) => {
    const newTemplate: ColorTemplate = {
      surveyBgColor: bgColor,
      completeBgColor: completeColor,
    };
    let updated = [newTemplate, ...localColorTemplates];
    if (updated.length > 5) updated = updated.slice(0, 5);
    setLocalColorTemplates(updated);

    const clientToken = props.ssoClient ? props.ssoClient.clientToken : "";

    props.setSurveyBgColorTemplates({
      clientToken: clientToken,
      colorTemplates: JSON.stringify(updated),
    });
  };

  const handleRemoveColorTemplate = (idx: number) => {
    const updated = localColorTemplates.filter((_, i) => i !== idx);
    setLocalColorTemplates(updated);

    const clientToken = props.ssoClient ? props.ssoClient.clientToken : "";
    props.setSurveyBgColorTemplates({
      clientToken: clientToken,
      colorTemplates: JSON.stringify(updated),
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={surveySettingsSchema}
    >
      {(formikBag) => {
        const selectedLanguageModel = props.languageModel.find(
          (item) => item.id === formikBag.values.languageModelId
        );
        const selectedLanguageName = selectedLanguageModel?.name ?? "";
        const isProofReadingModelValid = proofReadingModel.some((model) =>
          selectedLanguageName.includes(model)
        );

        useEffect(() => {
          // 現在選択されているモデルを取得
          const currentModel = formikBag.values.isLanguageModelSelectable
            ? props.languageModel.find(
                (model) => model.id === formikBag.values.languageModelId
              )
            : null;

          // 業種選択型モデルチェック
          const isIndustryModel = !!(
            currentModel?.code &&
            currentModel.code.toLowerCase().includes("industry")
          );

          // 無効化条件：最新版ランダムがオフ かつ 業種選択GPTが選択されている
          const shouldDisable =
            !formikBag.values.isRandomGenerateModel && isIndustryModel;

          // 無効化条件に該当し、かつImpressionPromptが有効な場合はオフにする
          if (
            shouldDisable &&
            formikBag.values.impressionPromptOptionsWithModels
              .isImpressionPrompt
          ) {
            formikBag.setFieldValue(
              "impressionPromptOptionsWithModels.isImpressionPrompt",
              false
            );
          }
        }, [
          formikBag.values.isRandomGenerateModel,
          formikBag.values.languageModelId,
          formikBag.values.isLanguageModelSelectable,
          props.languageModel,
        ]);

        return (
          <>
            <Tabs defaultActiveKey="basic">
              <Tabs.TabPane tab={i18n.t("basicSettingsTitle")} key="basic">
                <Form layout="vertical">
                  <div className="question-container">
                    <Row justify="center">
                      <Col span={18}>
                        {/* アンケートUI */}
                        <Form.Item
                          name="uiVersion"
                          label={i18n.t("survey.uiVersion")}
                        >
                          <Select
                            name="uiVersion"
                            options={UI_VERSION_OPTIONS}
                            onChange={(value) => {
                              formikBag.setFieldValue("uiVersion", value);
                              if (value === "v2") {
                                if (!formikBag.values.topPageNoteUiV2) {
                                  formikBag.setFieldValue(
                                    "topPageNoteUiV2",
                                    i18n.t("survey.defaultTopPageNoteUiV2")
                                  );
                                }
                                if (!formikBag.values.completionTitleUiV2) {
                                  formikBag.setFieldValue(
                                    "completionTitleUiV2",
                                    i18n.t("survey.defaultCompletionTitleUiV2")
                                  );
                                }
                                if (!formikBag.values.completionTextUiV2) {
                                  formikBag.setFieldValue(
                                    "completionTextUiV2",
                                    i18n.t("survey.defaultCompletionTextUiV2")
                                  );
                                }
                                if (!formikBag.values.editScreenCardTitleUiV2) {
                                  formikBag.setFieldValue(
                                    "editScreenCardTitleUiV2",
                                    i18n.t(
                                      "survey.defaultEditScreenCardTitleUiV2"
                                    )
                                  );
                                }
                                if (!formikBag.values.editScreenCardTextUiV2) {
                                  formikBag.setFieldValue(
                                    "editScreenCardTextUiV2",
                                    i18n.t(
                                      "survey.defaultEditScreenCardTextUiV2"
                                    )
                                  );
                                }
                              }
                            }}
                          />
                        </Form.Item>
                        {/* アンケートタイトル */}
                        <Form.Item
                          name="title"
                          label={i18n.t("survey.surveyTitle")}
                        >
                          <Input name="title" />
                        </Form.Item>
                        {/* ロゴ */}
                        <Form.Item name="logo" label={i18n.t("survey.logo")}>
                          <input
                            type="file"
                            onChange={handleFileChange}
                            ref={logoInputRef}
                          />
                          {logoUrl && (
                            <Row align="top">
                              <img
                                style={{ marginTop: "1em", maxWidth: "90%" }}
                                src={logoUrl}
                              />
                              <Button
                                shape="circle"
                                icon={<CloseOutlined />}
                                type="text"
                                style={{ marginTop: "0.5em" }}
                                onClick={handleFileRemove}
                              />
                            </Row>
                          )}
                        </Form.Item>
                        {/* 業種カテゴリ大 */}
                        <Form.Item
                          name="industryCategoryId"
                          label={
                            <span>
                              {i18n.t("survey.industryCategory")}
                              <span style={{ color: "red" }}>
                                {i18n.t("validation.required")}
                              </span>
                            </span>
                          }
                        >
                          <Select
                            name="industryCategoryId"
                            placeholder={i18n.t(
                              "survey.industryCategoryPlaceholder"
                            )}
                            options={props.industryCategories.map((cat) => ({
                              value: cat.id,
                              label: cat.name,
                            }))}
                            onChange={(value) => {
                              formikBag.setFieldValue(
                                "industryCategoryId",
                                value
                              );
                              // 業種が解除された場合、業種選択モデルが選択されていたらリセット
                              if (!value || value.trim() === "") {
                                const currentModel = props.languageModel.find(
                                  (model) =>
                                    model.id ===
                                    formikBag.values.languageModelId
                                );
                                if (
                                  currentModel &&
                                  currentModel.code &&
                                  currentModel.code
                                    .toLowerCase()
                                    .includes("industry")
                                ) {
                                  formikBag.setFieldValue(
                                    "languageModelId",
                                    props.languageModel.find(
                                      (item) =>
                                        item.code === DEFAULT_LANGUAGE_MODEL
                                    )?.id ?? null
                                  );
                                  formikBag.setFieldValue(
                                    "isLanguageModelSelectable",
                                    false
                                  );
                                }
                              }
                            }}
                          />
                        </Form.Item>
                        {/* 業種カテゴリ小 */}
                        <Form.Item
                          name="industrySubcategory"
                          label={i18n.t("survey.industrySubcategory")}
                        >
                          <Input
                            name="industrySubcategory"
                            placeholder={i18n.t(
                              "survey.industrySubcategoryPlaceholder"
                            )}
                            onChange={(e) => {
                              const value = e.target.value;
                              formikBag.setFieldValue(
                                "industrySubcategory",
                                value
                              );
                              // サブカテゴリが解除された場合、業種選択モデルが選択されていたらリセット
                              if (!value || value.trim() === "") {
                                const currentModel = props.languageModel.find(
                                  (model) =>
                                    model.id ===
                                    formikBag.values.languageModelId
                                );
                                if (
                                  currentModel &&
                                  currentModel.code &&
                                  currentModel.code
                                    .toLowerCase()
                                    .includes("industry")
                                ) {
                                  formikBag.setFieldValue(
                                    "languageModelId",
                                    props.languageModel.find(
                                      (item) =>
                                        item.code === DEFAULT_LANGUAGE_MODEL
                                    )?.id ?? null
                                  );
                                  formikBag.setFieldValue(
                                    "isLanguageModelSelectable",
                                    false
                                  );
                                }
                              }
                            }}
                          />
                        </Form.Item>
                        {/* 全ページロゴ・店舗名表示 */}
                        <Form.Item
                          name="isViewLogoAllPages"
                          label={i18n.t("survey.isViewLogoAllPages")}
                        >
                          <Switch
                            checked={formikBag.values.isViewLogoAllPages}
                            onChange={(checked: boolean) => {
                              formikBag.setFieldValue(
                                "isViewLogoAllPages",
                                checked
                              );
                            }}
                          />
                        </Form.Item>
                        {formikBag.values.uiVersion !== "v2" ? (
                          <React.Fragment key="ui-version-v1">
                            {/* アンケートTOP見出し */}
                            <Form.Item
                              name="topPageTitle"
                              label={i18n.t("survey.topPageTitle")}
                            >
                              <Input name="topPageTitle" />
                            </Form.Item>
                            {/* アンケートTOP注意事項 */}
                            <Form.Item
                              name="topPageNote"
                              label={i18n.t("survey.topPageNote")}
                            >
                              <RichEditor
                                value={formikBag.values.topPageNote || ""}
                                onChange={(value) => {
                                  formikBag.setFieldValue("topPageNote", value);
                                }}
                              />
                            </Form.Item>
                          </React.Fragment>
                        ) : (
                          <React.Fragment key="ui-version-v2">
                            {/* アンケート回答所要時間 */}
                            <Form.Item
                              name="isViewAnswerTimeUiV2"
                              label={i18n.t("survey.isViewAnswerTimeUiV2")}
                            >
                              <Switch
                                checked={formikBag.values.isViewAnswerTimeUiV2}
                                onChange={(checked: boolean) => {
                                  formikBag.setFieldValue(
                                    "isViewAnswerTimeUiV2",
                                    checked
                                  );
                                }}
                              />
                              {formikBag.values.isViewAnswerTimeUiV2 && (
                                <div
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    marginLeft: "12px",
                                  }}
                                >
                                  <Form.Item
                                    name="answerTimeMinutesUiV2"
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select
                                      name="answerTimeMinutesUiV2"
                                      options={UI_V2_ANSWER_TIME_OPTIONS}
                                      style={{ width: "90px" }}
                                    />
                                  </Form.Item>
                                </div>
                              )}
                            </Form.Item>
                            {/* アンケートTOP注意事項 */}
                            <Form.Item
                              name="topPageNoteUiV2"
                              label={i18n.t("survey.topPageNoteUiV2")}
                            >
                              <RichEditor
                                value={formikBag.values.topPageNoteUiV2 || ""}
                                onChange={(value) => {
                                  formikBag.setFieldValue(
                                    "topPageNoteUiV2",
                                    value
                                  );
                                }}
                              />
                            </Form.Item>
                            {/* アンケート完了画面見出し */}
                            <Form.Item
                              name="completionTitleUiV2"
                              label={i18n.t("survey.completionTitleUiV2")}
                            >
                              <RichEditor
                                value={
                                  formikBag.values.completionTitleUiV2 || ""
                                }
                                onChange={(value) => {
                                  formikBag.setFieldValue(
                                    "completionTitleUiV2",
                                    value
                                  );
                                }}
                              />
                            </Form.Item>
                            {/* アンケート完了画面文章 */}
                            <Form.Item
                              name="completionTextUiV2"
                              label={i18n.t("survey.completionTextUiV2")}
                            >
                              <RichEditor
                                value={
                                  formikBag.values.completionTextUiV2 || ""
                                }
                                onChange={(value) => {
                                  formikBag.setFieldValue(
                                    "completionTextUiV2",
                                    value
                                  );
                                }}
                              />
                            </Form.Item>
                            {/* アンケート編集画面カード */}
                            <Form.Item
                              name="isEditScreenCardUiV2"
                              label={i18n.t("survey.isEditScreenCardUiV2")}
                            >
                              <Switch
                                checked={formikBag.values.isEditScreenCardUiV2}
                                onChange={(checked: boolean) => {
                                  formikBag.setFieldValue(
                                    "isEditScreenCardUiV2",
                                    checked
                                  );
                                }}
                              />
                              {formikBag.values.isEditScreenCardUiV2 && (
                                <div
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    marginLeft: "16px",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "28px",
                                      height: "28px",
                                      borderRadius: "2px",
                                      background:
                                        formikBag.values
                                          .editScreenCardBgColorHexUiV2 ||
                                        "#fffbe6",
                                      display: "inline-block",
                                      cursor: "pointer",
                                      border: "1px solid #ccc",
                                      marginRight: "8px",
                                    }}
                                    onClick={() =>
                                      setEditScreenCardColorPicker(true)
                                    }
                                  />
                                  <input
                                    style={{ width: "100px" }}
                                    value={
                                      formikBag.values
                                        .editScreenCardBgColorHexUiV2 ||
                                      "#fffbe6"
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (
                                        /^#([A-Fa-f0-9]{6})$/.test(value) ||
                                        value === ""
                                      ) {
                                        formikBag.setFieldValue(
                                          "editScreenCardBgColorHexUiV2",
                                          value
                                        );
                                      }
                                    }}
                                    maxLength={7}
                                  />
                                  <ColorPickerPopup
                                    visible={editScreenCardColorPicker}
                                    onClose={() =>
                                      setEditScreenCardColorPicker(false)
                                    }
                                    color={
                                      formikBag.values
                                        .editScreenCardBgColorHexUiV2 ||
                                      "#fffbe6"
                                    }
                                    onChangeComplete={(color: {
                                      hex: string;
                                    }) =>
                                      formikBag.setFieldValue(
                                        "editScreenCardBgColorHexUiV2",
                                        color.hex
                                      )
                                    }
                                  />
                                </div>
                              )}
                            </Form.Item>
                            {/* アンケート編集画面カード見出し */}
                            <Form.Item
                              name="editScreenCardTitleUiV2"
                              label={i18n.t("survey.editScreenCardTitleUiV2")}
                            >
                              <RichEditor
                                value={
                                  formikBag.values.editScreenCardTitleUiV2 || ""
                                }
                                onChange={(value) => {
                                  formikBag.setFieldValue(
                                    "editScreenCardTitleUiV2",
                                    value
                                  );
                                }}
                              />
                            </Form.Item>
                            {/* アンケート編集画面カード内文章 */}
                            <Form.Item
                              name="editScreenCardTextUiV2"
                              label={i18n.t("survey.editScreenCardTextUiV2")}
                            >
                              <RichEditor
                                value={
                                  formikBag.values.editScreenCardTextUiV2 || ""
                                }
                                onChange={(value) => {
                                  formikBag.setFieldValue(
                                    "editScreenCardTextUiV2",
                                    value
                                  );
                                }}
                              />
                            </Form.Item>
                          </React.Fragment>
                        )}
                        {/* ランダム設定 */}
                        <Form.Item
                          name="randomDisplay"
                          label={i18n.t("survey.randomDisplay")}
                        >
                          <Switch
                            checked={formikBag.values.randomDisplay}
                            onChange={(checked: boolean) => {
                              if (checked) {
                                setIsRandomDisplayModalOpen(true);
                              } else {
                                // ランダム設定をオフにした場合、カウントもデフォルト値にリセットしモーダルも閉じる
                                formikBag.setFieldValue("randomDisplay", false);
                                formikBag.setFieldValue(
                                  "randomDisplayCount",
                                  DEFAULT_RANDOM_COUNT
                                );
                                setIsRandomDisplayModalOpen(false);
                              }
                            }}
                          />
                          {formikBag.values.randomDisplay && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <span className="toggle-label">
                                {i18n.t("survey.randomDisplayCountLabel")}
                              </span>
                              <Form.Item
                                name="randomDisplayCount"
                                style={{
                                  marginBottom: 0,
                                  marginLeft: "8px",
                                }}
                              >
                                <Select
                                  name="randomDisplayCount"
                                  options={randomDisplayOptions}
                                  style={{ width: "70px" }}
                                />
                              </Form.Item>
                            </div>
                          )}
                        </Form.Item>
                        <RandomDisplaySetting
                          visible={isRandomDisplayModalOpen}
                          closeModal={() => setIsRandomDisplayModalOpen(false)}
                          onSave={(randomDisplayCount: number) => {
                            // モーダルで保存ボタンが押されたときにformikBagを更新
                            formikBag.setFieldValue("randomDisplay", true);
                            formikBag.setFieldValue(
                              "randomDisplayCount",
                              randomDisplayCount
                            );
                            setIsRandomDisplayModalOpen(false);
                          }}
                          currentRandomDisplayCount={
                            formikBag.values.randomDisplayCount ??
                            DEFAULT_RANDOM_COUNT
                          }
                        />
                        {/* QAシャッフル */}
                        <Form.Item
                          name="randomQuestionsOrder"
                          label={i18n.t("survey.randomQuestionsOrder")}
                        >
                          <Switch
                            checked={formikBag.values.randomQuestionsOrder}
                            onChange={(checked: boolean) => {
                              formikBag.setFieldValue(
                                "randomQuestionsOrder",
                                checked
                              );
                            }}
                          />
                        </Form.Item>
                        {/* アルゴリズム設定 */}
                        <Form.Item
                          name="isLanguageModelSelectable"
                          label={i18n.t("survey.selectGptModel")}
                        >
                          <Switch
                            checked={formikBag.values.isLanguageModelSelectable}
                            onChange={(checked: boolean) => {
                              if (checked) {
                                setIsLanguageModel(true);
                              } else {
                                formikBag.setFieldValue(
                                  "isLanguageModelSelectable",
                                  false
                                );
                              }
                            }}
                          />
                          {formikBag.values.isLanguageModelSelectable && (
                            <>
                              <span className="toggle-label">
                                {formikBag.values.isRandomGenerateModel
                                  ? i18n.t("survey.randomGenerateModelText")
                                  : selectedLanguageName}
                              </span>
                              <span className="toggle-label">
                                {(selectedLanguageName &&
                                  isProofReadingModelValid &&
                                  formikBag.values.isProofreading) ||
                                (formikBag.values.isRandomGenerateModel &&
                                  formikBag.values.isProofreading)
                                  ? i18n.t("survey.selectGptModelOption")
                                  : ""}
                              </span>
                              <span className="toggle-label">
                                {formikBag.values.generateTemperature
                                  ? i18n.t("survey.selectGenerateTemperature", {
                                      temperature: Number(
                                        formikBag.values.generateTemperature
                                      ).toFixed(1),
                                    })
                                  : ""}
                              </span>
                            </>
                          )}
                        </Form.Item>
                        <SelectGptModelModal
                          visible={isLanguageModel}
                          closeModal={() => setIsLanguageModel(false)}
                          selectModelId={formikBag.values.languageModelId}
                          isRandomGenerateModel={
                            formikBag.values.isRandomGenerateModel
                          }
                          industryCategoryId={
                            formikBag.values.industryCategoryId
                          }
                          industrySubcategory={
                            formikBag.values.industrySubcategory
                          }
                          generateTemperature={
                            formikBag.values.generateTemperature
                          }
                          onChange={(value) => {
                            formikBag.setFieldValue(
                              "isRandomGenerateModel",
                              value
                            );
                          }}
                          onSave={(
                            selectedModelId,
                            isRandom,
                            isProofreading,
                            generateTemperature
                          ) => {
                            formikBag.setFieldValue(
                              "languageModelId",
                              selectedModelId
                            );
                            formikBag.setFieldValue(
                              "isRandomGenerateModel",
                              isRandom
                            );
                            formikBag.setFieldValue(
                              "isProofreading",
                              isProofreading
                            );
                            formikBag.setFieldValue(
                              "generateTemperature",
                              generateTemperature
                            );
                            formikBag.setFieldValue(
                              "isLanguageModelSelectable",
                              true
                            );
                            setIsLanguageModel(false);
                          }}
                        />
                        {/* 印象変換 */}
                        <Form.Item
                          name="impressionPromptOptionsWithModels.isImpressionPrompt"
                          label={i18n.t("survey.selectImpressionPrompt")}
                        >
                          {(() => {
                            // 現在選択されているモデルを取得
                            const currentModel = formikBag.values
                              .isLanguageModelSelectable
                              ? props.languageModel.find(
                                  (model) =>
                                    model.id ===
                                    formikBag.values.languageModelId
                                )
                              : null;
                            // 業種選択型モデルチェック
                            const isIndustryModel = !!(
                              currentModel?.code &&
                              currentModel.code
                                .toLowerCase()
                                .includes("industry")
                            );
                            // 無効化条件：最新版ランダムがオフ かつ 業種選択GPTが選択されている
                            const shouldDisable =
                              !formikBag.values.isRandomGenerateModel &&
                              isIndustryModel;

                            const switchComponent = (
                              <Switch
                                checked={
                                  formikBag.values
                                    .impressionPromptOptionsWithModels
                                    .isImpressionPrompt
                                }
                                disabled={shouldDisable}
                                onChange={(checked: boolean) => {
                                  if (checked) {
                                    setIsImpressionPrompt(true);
                                  } else {
                                    formikBag.setFieldValue(
                                      "impressionPromptOptionsWithModels.isImpressionPrompt",
                                      false
                                    );
                                  }
                                }}
                              />
                            );
                            return shouldDisable ? (
                              <Tooltip
                                title={i18n.t(
                                  "survey.impressionSelectIndustryModelTooltip"
                                )}
                              >
                                <span>{switchComponent}</span>
                              </Tooltip>
                            ) : (
                              switchComponent
                            );
                          })()}
                          <span className="toggle-label">
                            {getImpressionModeText(
                              formikBag.values.impressionPromptOptionsWithModels
                                .isImpressionPrompt,
                              formikBag.values.impressionPromptOptionsWithModels
                                .impressionPromptMode
                            )}
                          </span>
                        </Form.Item>
                        <SelectImpressionPromptModal
                          errors={formikBag.errors}
                          visible={isImpressionPrompt}
                          closeModal={() => setIsImpressionPrompt(false)}
                          currentOptions={
                            formikBag.values.impressionPromptOptionsWithModels
                          }
                          onSave={(options) => {
                            formikBag.setFieldValue(
                              "impressionPromptOptionsWithModels.impressionPromptMode",
                              options.impressionPromptMode
                            );
                            formikBag.setFieldValue(
                              "impressionPromptOptionsWithModels.impressionPromptModels",
                              options.impressionPromptModels
                            );
                            formikBag.setFieldValue(
                              "impressionPromptOptionsWithModels.isImpressionPrompt",
                              true
                            );
                            setIsImpressionPrompt(false);
                          }}
                        />
                        {/* プロンプト追加注意 */}
                        <Form.Item
                          name="customPrompt"
                          label={i18n.t("survey.customPrompt")}
                        >
                          <Switch
                            checked={formikBag.values.customPrompt}
                            onChange={(checked: boolean) => {
                              formikBag.setFieldValue("customPrompt", checked);
                            }}
                          />
                        </Form.Item>
                        <Form.Item name="customPromptText">
                          <Input.TextArea name="customPromptText" />
                        </Form.Item>
                        {/* 言語設定 */}
                        <Form.Item
                          name="isLanguageSettingsSelectable"
                          label={i18n.t("survey.selectLanguage")}
                        >
                          <Switch
                            checked={
                              formikBag.values.isLanguageSettingsSelectable
                            }
                            onChange={(checked: boolean) => {
                              if (checked) {
                                setIsLanguageSettings(true);
                              } else {
                                formikBag.setFieldValue(
                                  "isLanguageSettingsSelectable",
                                  false
                                );
                              }
                            }}
                          />
                          {formikBag.values.isLanguageSettingsSelectable && (
                            <span className="toggle-label">
                              {props.languageSettings.find(
                                (item) =>
                                  item.id ===
                                  formikBag.values.languageSettingsId
                              )?.name ?? ""}
                            </span>
                          )}
                        </Form.Item>
                        <SelectLanguageModal
                          visible={isLanguageSettings}
                          closeModal={() => setIsLanguageSettings(false)}
                          selectLanguageId={formikBag.values.languageSettingsId}
                          onSave={(selectedLanguageId) => {
                            formikBag.setFieldValue(
                              "languageSettingsId",
                              selectedLanguageId
                            );
                            formikBag.setFieldValue(
                              "isLanguageSettingsSelectable",
                              true
                            );
                            setIsLanguageSettings(false);
                          }}
                        />
                        {/* 特典オプション */}
                        <Form.Item
                          name="isEmailOption"
                          label={i18n.t("survey.emailOptions")}
                        >
                          <Switch
                            checked={formikBag.values.isEmailOption}
                            onChange={(checked: boolean) => {
                              if (checked) {
                                setIsEmailOptionsModalOpen(true);
                              } else {
                                formikBag.setFieldValue("isEmailOption", false);
                              }
                            }}
                          />
                          {formikBag.values.isEmailOption && (
                            <>
                              <span className="toggle-label">
                                {i18n.t("survey.emailSendTiming")}:
                                {formikBag.values.emailDelayMinutes === 0
                                  ? i18n.t("survey.emailSendTimingImmediate")
                                  : `${
                                      formikBag.values.emailDelayMinutes
                                    }${i18n.t(
                                      "survey.emailSendTimingDelayMinutes"
                                    )}`}
                              </span>
                              <span className="toggle-label">
                                {i18n.t("survey.fortuneSettingsType")}:
                                {props.fortuneSettingsTypeList.find(
                                  (item) =>
                                    item.id ===
                                    formikBag.values.fortuneSettingsTypeId
                                )?.name ?? ""}
                              </span>
                            </>
                          )}
                        </Form.Item>
                        <EmailOptionModal
                          visible={isEmailOptionsModalOpen}
                          closeModal={() => {
                            setIsEmailOptionsModalOpen(false);
                          }}
                          onSave={(
                            emailDelayMinutes,
                            fortuneSettingsTypeId
                          ) => {
                            // モーダルで保存ボタンが押されたときにformikBagを更新
                            formikBag.setFieldValue("isEmailOption", true);
                            formikBag.setFieldValue(
                              "emailDelayMinutes",
                              emailDelayMinutes
                            );
                            formikBag.setFieldValue(
                              "fortuneSettingsTypeId",
                              fortuneSettingsTypeId
                            );
                            setIsEmailOptionsModalOpen(false);
                          }}
                          currentEmailDelayMinutes={
                            formikBag.values.emailDelayMinutes
                          }
                          currentFortuneSettingsTypeId={
                            formikBag.values.fortuneSettingsTypeId
                          }
                          fortuneSettingsTypeList={
                            props.fortuneSettingsTypeList
                          }
                        />
                      </Col>
                    </Row>
                  </div>
                </Form>
              </Tabs.TabPane>
              <Tabs.TabPane tab={i18n.t("designSetting.title")} key="design">
                <Form layout="vertical">
                  <div className="question-container">
                    <Row justify="center">
                      <Col span={18}>
                        {/* 背景色設定 */}
                        <div style={{ marginBottom: "40px" }}>
                          <h3
                            style={{ fontWeight: "bold", marginBottom: "20px" }}
                          >
                            {i18n.t("designSetting.backgroundColor")}
                          </h3>
                          {/* アンケート画面 */}
                          <div
                            style={{
                              marginBottom: "30px",
                              paddingLeft: "16px",
                              position: "relative",
                            }}
                          >
                            <h4 style={{ fontWeight: "bold" }}>
                              {i18n.t("designSetting.surveyDisplay")}
                            </h4>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <div
                                style={{
                                  width: "35px",
                                  height: "35px",
                                  borderRadius: "2px",
                                  background:
                                    formikBag.values.bgColorHex || "#f5a623",
                                  display: "inline-block",
                                  cursor: "pointer",
                                  border: "1px solid #ccc",
                                  marginRight: "12px",
                                  position: "relative",
                                }}
                                onClick={() => {
                                  setSurveyBgColorPicker(true);
                                }}
                              />
                              <input
                                style={{ width: "120px", marginRight: "12px" }}
                                value={formikBag.values.bgColorHex || "#f5a623"}
                                pattern="^#([A-Fa-f0-9]{6})$"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    /^#([A-Fa-f0-9]{6})$/.test(value) ||
                                    value === ""
                                  ) {
                                    formikBag.setFieldValue(
                                      "bgColorHex",
                                      value
                                    );
                                  }
                                }}
                                maxLength={7}
                              />
                              <ColorPickerPopup
                                visible={surveyBgColorPicker}
                                onClose={() => setSurveyBgColorPicker(false)}
                                color={formikBag.values.bgColorHex || "#f5a623"}
                                onChangeComplete={(color: { hex: string }) =>
                                  formikBag.setFieldValue(
                                    "bgColorHex",
                                    color.hex
                                  )
                                }
                              />
                            </div>
                          </div>
                          {/* アンケート終了画面 */}
                          <div
                            style={{
                              paddingLeft: "16px",
                              position: "relative",
                            }}
                          >
                            <h4 style={{ fontWeight: "bold" }}>
                              {i18n.t("designSetting.surveyCompleteDisplay")}
                            </h4>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <div
                                style={{
                                  width: "35px",
                                  height: "35px",
                                  borderRadius: "2px",
                                  background:
                                    formikBag.values.completeBgColorHex ||
                                    "#d0cfcd",
                                  display: "inline-block",
                                  cursor: "pointer",
                                  border: "1px solid #ccc",
                                  marginRight: "12px",
                                }}
                                onClick={() => {
                                  setSurveyCompleteBgColorPicker(true);
                                }}
                              />
                              <input
                                style={{ width: "120px", marginRight: "12px" }}
                                value={
                                  formikBag.values.completeBgColorHex ||
                                  "#d0cfcd"
                                }
                                pattern="^#([A-Fa-f0-9]{6})$"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Only update if valid hex color or empty (to allow clearing)
                                  if (
                                    /^#([A-Fa-f0-9]{6})$/.test(value) ||
                                    value === ""
                                  ) {
                                    formikBag.setFieldValue(
                                      "completeBgColorHex",
                                      value
                                    );
                                  }
                                }}
                                maxLength={7}
                              />
                              <ColorPickerPopup
                                visible={surveyCompleteBgColorPicker}
                                onClose={() =>
                                  setSurveyCompleteBgColorPicker(false)
                                }
                                color={
                                  formikBag.values.completeBgColorHex ||
                                  "#d0cfcd"
                                }
                                onChangeComplete={(color: { hex: string }) =>
                                  formikBag.setFieldValue(
                                    "completeBgColorHex",
                                    color.hex
                                  )
                                }
                              />
                            </div>
                          </div>
                          {/* テンプレ保存ボタン */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginTop: "20px",
                              marginBottom: "30px",
                              paddingLeft: "16px",
                            }}
                          >
                            <Button
                              type="primary"
                              // formikBag.valuesから現在の色を渡す
                              onClick={() =>
                                handleSaveTemplate(
                                  formikBag.values.bgColorHex || "#f5a623",
                                  formikBag.values.completeBgColorHex ||
                                    "#d0cfcd"
                                )
                              }
                              style={{
                                paddingLeft: "16px",
                                paddingTop: "2px",
                                paddingBottom: "2px",
                              }}
                            >
                              <FolderOpenOutlined />
                              {i18n.t("backgroundColorTemplates.saveButton")}
                            </Button>
                          </div>
                          {/* テンプレート */}
                          <div
                            style={{
                              marginBottom: "30px",
                              paddingLeft: "16px",
                            }}
                          >
                            <h4 style={{ fontWeight: "bold", margin: 0 }}>
                              {i18n.t("backgroundColorTemplates.title")}
                            </h4>
                            <div
                              style={{
                                marginTop: "25px",
                                display: "flex",
                                gap: "45px",
                                background: "#f5f5f5",
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                padding: "20px",
                                height: "100px",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {localColorTemplates.length === 0 && (
                                <div
                                  style={{
                                    color: "#888",
                                    height: "60px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  {i18n.t("backgroundColorTemplates.nothing")}
                                </div>
                              )}
                              {localColorTemplates.map((tpl, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    formikBag.setFieldValue(
                                      "bgColorHex",
                                      tpl.surveyBgColor
                                    );
                                    formikBag.setFieldValue(
                                      "completeBgColorHex",
                                      tpl.completeBgColor
                                    );
                                  }}
                                >
                                  {/* バツボタン */}
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CloseOutlined />}
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      right: 0,
                                      zIndex: 2,
                                      padding: 0,
                                      lineHeight: 1,
                                      width: 24,
                                      height: 24,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "rgba(255,255,255,0.7)",
                                      borderRadius: "50%",
                                      transform: "translate(50%, -50%)",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveColorTemplate(idx);
                                    }}
                                  />
                                  {/* 上下2色で表示（1セット） */}
                                  <div
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      border: "1px solid #ccc",
                                      marginBottom: 4,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        height: "50%",
                                        background: tpl.surveyBgColor,
                                      }}
                                    />
                                    <div
                                      style={{
                                        height: "50%",
                                        background: tpl.completeBgColor,
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* プログレスバー */}
                        <div style={{ marginBottom: "30px" }}>
                          <h3 style={{ fontWeight: "bold" }}>
                            {i18n.t("progressBar.title")}
                          </h3>
                          <Checkbox
                            checked={formikBag.values.isViewProgressBar}
                            onChange={(e) =>
                              formikBag.setFieldValue(
                                "isViewProgressBar",
                                e.target.checked
                              )
                            }
                          >
                            {i18n.t("progressBar.view")}
                          </Checkbox>
                        </div>
                        {/* 音声入力アイコンサイズ */}
                        <div style={{ marginBottom: "30px" }}>
                          <h3 style={{ fontWeight: "bold" }}>
                            {i18n.t("voiceInputIconSize.title")}
                          </h3>
                          <label>
                            <input
                              type="radio"
                              name="voiceInputIconSize"
                              value={"normal"}
                              checked={
                                formikBag.values.voiceInputIconSize === "normal"
                              }
                              onChange={() =>
                                formikBag.setFieldValue(
                                  "voiceInputIconSize",
                                  "normal"
                                )
                              }
                            />
                            {i18n.t("voiceInputIconSize.normal")}
                          </label>
                          <label style={{ marginLeft: "1em" }}>
                            <input
                              type="radio"
                              name="voiceInputIconSize"
                              value={"large"}
                              checked={
                                formikBag.values.voiceInputIconSize === "large"
                              }
                              onChange={() =>
                                formikBag.setFieldValue(
                                  "voiceInputIconSize",
                                  "large"
                                )
                              }
                            />
                            {i18n.t("voiceInputIconSize.large")}
                          </label>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </Tabs.TabPane>
            </Tabs>
            <Row
              justify="end"
              style={{ marginBottom: "10px", marginRight: "20px" }}
            >
              <SaveButton
                type="primary"
                htmlType="submit"
                disabled={!formikBag.values.industryCategoryId}
                onClick={() => formikBag.handleSubmit()}
              />
            </Row>
          </>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    survey: state.survey.survey!,
    getLoading: state.survey.getLoading,
    languageModel: state.survey.languageModel,
    getLanguageModelLoading: state.survey.getLanguageModelLoading,
    languageSettings: state.survey.languageSettings,
    getLanguageSettingsLoading: state.survey.getLanguageSettingsListLoading,
    impressionModels: state.survey.impressionModels,
    impressionModelsLoading: state.survey.impressionModelsLoading,
    selectableImpressionModels: state.survey.selectableImpressionModels,
    selectableImpressionModelsLoading:
      state.survey.selectableImpressionModelsLoading,
    fortuneSettingsTypeList: state.survey.fortuneSettingsTypeList,
    getFortuneSettingsTypeListLoading:
      state.survey.fortuneSettingsTypeListLoading,
    ssoClient: state.client.ssoClient,
    colorTemplates: state.survey.colorTemplates ?? "",
    industryCategories: state.survey.industryCategories,
  };
};

const mapDispatchToProps = {
  setPage,
  getSurvey,
  editSurveySettings,
  addSurveys,
  uploadLogo,
  getLanguageModelListAll,
  getLanguageSettingsListAll,
  getImpressionModels,
  clearImpressionModels,
  getSelectableImpressionModels,
  getFortuneSettingsTypeListAll,
  setSurveyBgColorTemplates,
  getSurveyBgColorTemplates,
  getIndustryCategories,
};

export default connect(mapStateToProps, mapDispatchToProps)(SurveySettings);
